import { EditOutlined, PlusSquareOutlined } from '@ant-design/icons'
import { Button, Drawer, Form, Select, Table, Tag, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { useCallback, useEffect, useState } from 'react'

import { Shrine } from '@a4b/api/src/modules/Temple/types'
import { useAppContext } from '@/components/AppContext'
import { A4B_APPS, PUJA_PRESIGN_URL, YODA_CDN_URL } from '@/utils/constants'
import { CARD_TYPES } from '@/modules/shraddha-shorts/constants'
import {
    StoreOption,
    getStoreServiceType,
    isRecommendationCard,
    transformCardContentsResponse,
    transformRecommendationsResponse,
    transformStoreListResponse,
} from '@/modules/shraddha-shorts/utils/helper'
import {
    formatCardContentCreatePayload,
    formatCardContentUpdatePayload,
    parseCardContentToFormFields,
} from '../../ManageEventCalendar/utils/helper'
import CardContentForm from './CardContentForm'

type CardContentRow = Shrine.CardContent

interface CardContentsDrawerProps {
    visible: boolean
    onClose: () => void
    card: Shrine.Card
    eventCalendarId: string
}

const DrawerActions: React.FC<{
    isRecommendationCard: boolean
    savingRecommendations: boolean
    onSaveRecommendations: () => void
    onAddContent: () => void
}> = ({
    isRecommendationCard,
    savingRecommendations,
    onSaveRecommendations,
    onAddContent,
}) => {
        if (isRecommendationCard) {
            return (
                <Button
                    type='primary'
                    loading={savingRecommendations}
                    onClick={onSaveRecommendations}
                >
                    Save
                </Button>
            )
        }

        return (
            <Button type='primary' icon={<PlusSquareOutlined />} onClick={onAddContent}>
                Add Content
            </Button>
        )
    }

const CardContentsDrawer: React.FC<CardContentsDrawerProps> = ({
    visible,
    onClose,
    card,
    eventCalendarId,
}) => {
    const { networkInstance, countryState, languageState, app } = useAppContext()
    const [form] = Form.useForm()

    const cdnUrl = YODA_CDN_URL
    const preSignUrl = PUJA_PRESIGN_URL

    const [loadingContents, setLoadingContents] = useState(false)
    const [cardContents, setCardContents] = useState<CardContentRow[]>([])

    const [loadingStores, setLoadingStores] = useState(false)
    const [storeOptions, setStoreOptions] = useState<StoreOption[]>([])

    const [loadingRecommendations, setLoadingRecommendations] = useState(false)
    const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([])
    const [savingRecommendations, setSavingRecommendations] = useState(false)

    const [contentDrawerOpen, setContentDrawerOpen] = useState(false)
    const [editingContent, setEditingContent] = useState<CardContentRow | null>(
        null,
    )
    const [submittingContent, setSubmittingContent] = useState(false)

    const isRecommendation = isRecommendationCard(card?.card_type)
    const expectedStoreServiceType = getStoreServiceType(card?.card_type)

    const fetchCardContents = useCallback(async () => {
        if (!card?.id || !eventCalendarId) {
            console.warn('Missing card id or event calendar id')
            return
        }
        setLoadingContents(true)
        try {
            const response =
                await networkInstance.clientWithHeaders.shrineApi.getCardContent(
                    eventCalendarId,
                    card.id,
                )
            const normalized = transformCardContentsResponse(response)
            setCardContents(normalized)
        } catch (err: any) {
            const errorMsg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch card contents'
            message.error(errorMsg)
            console.error('Card contents fetch error:', err)
        } finally {
            setLoadingContents(false)
        }
    }, [networkInstance, eventCalendarId, card?.id])

    const fetchPujaStores = useCallback(async () => {
        const { monetisationPujaApi, monetisationAstroPujaApi } =
            networkInstance.clientWithHeaders
        const pujaApi =
            app === A4B_APPS.SRIMANDIR
                ? monetisationPujaApi
                : monetisationAstroPujaApi

        return await pujaApi.storesApi.getStoreList(
            { limit: 2000, offset: 0 },
            countryState?.countryCode,
            true,
            undefined,
            undefined,
            undefined,
            'true',
        )
    }, [networkInstance, app, countryState?.countryCode])

    const fetchOtherStores = useCallback(async () => {
        return await networkInstance.clientWithHeaders.monetisationOfferingsApi.storesApi.getStoreList(
            {},
            'dropdown',
            undefined,
            true,
            undefined,
        )
    }, [networkInstance])

    const fetchStoresForRecommendations = useCallback(async () => {
        if (!expectedStoreServiceType) return
        setLoadingStores(true)
        try {
            let res: any

            if (card?.card_type === CARD_TYPES.RECOMMEND_PUJA) {
                res = await fetchPujaStores()
            } else {
                res = await fetchOtherStores()
            }

            const normalized = transformStoreListResponse(
                res,
                expectedStoreServiceType,
            )
            setStoreOptions(normalized)
        } catch (err: any) {
            const errorMsg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch store list'
            message.error(errorMsg)
            console.error('Store list fetch error:', err)
            setStoreOptions([])
        } finally {
            setLoadingStores(false)
        }
    }, [
        expectedStoreServiceType,
        card?.card_type,
        fetchPujaStores,
        fetchOtherStores,
    ])

    const fetchRecommendations = useCallback(async () => {
        if (!card?.id || !eventCalendarId) return
        setLoadingRecommendations(true)
        try {
            const res =
                await networkInstance.clientWithHeaders.shrineApi.getRecommendations(
                    eventCalendarId,
                    card.id,
                )

            const entityIds = transformRecommendationsResponse(res, card.id)
            setSelectedEntityIds(entityIds)
        } catch (err: any) {
            const errorMsg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch recommendations'
            message.error(errorMsg)
            console.error('Recommendations fetch error:', err)
            setSelectedEntityIds([])
        } finally {
            setLoadingRecommendations(false)
        }
    }, [networkInstance, eventCalendarId, card?.id])

    const saveRecommendationsApi = useCallback(async () => {
        return await networkInstance.clientWithHeaders.shrineApi.createRecommendations(
            eventCalendarId,
            card.id,
            {
                card_id: card.id,
                entity_ids: selectedEntityIds,
            },
        )
    }, [networkInstance, eventCalendarId, card?.id, selectedEntityIds])

    const saveRecommendations = useCallback(async () => {
        if (!card?.id || !eventCalendarId) return
        setSavingRecommendations(true)
        try {
            await saveRecommendationsApi()
            onClose()
            message.success('Recommendations saved')
        } catch (err: any) {
            const errorMsg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to save recommendations'
            message.error(errorMsg)
            console.error('Recommendations save error:', err)
        } finally {
            setSavingRecommendations(false)
        }
    }, [saveRecommendationsApi, eventCalendarId, card?.id, onClose])

    useEffect(() => {
        if (visible) {
            if (isRecommendation) {
                fetchStoresForRecommendations()
                fetchRecommendations()
            } else {
                fetchCardContents()
            }
        }
    }, [
        visible,
        fetchCardContents,
        card?.id,
        isRecommendation,
        fetchStoresForRecommendations,
        fetchRecommendations,
    ])

    const openCreateContent = useCallback(() => {
        setEditingContent(null)
        form.resetFields()
        form.setFieldsValue({
            active: true,
        })
        setContentDrawerOpen(true)
    }, [form])

    const openEditContent = useCallback(
        (record: CardContentRow) => {
            setEditingContent(record)
            const formFields = parseCardContentToFormFields(record)
            form.setFieldsValue(formFields)
            setContentDrawerOpen(true)
        },
        [form],
    )

    const closeContentDrawer = useCallback(() => {
        setContentDrawerOpen(false)
        setEditingContent(null)
        form.resetFields()
    }, [form])

    const handleSubmitContent = useCallback(
        async (values: any) => {
            if (!card?.id) return
            if (submittingContent) return
            setSubmittingContent(true)
            try {
                if (editingContent?.id) {
                    const payload = formatCardContentUpdatePayload(
                        values,
                        card.id,
                        editingContent.id,
                    )
                    await networkInstance.clientWithHeaders.shrineApi.updateCardContent(
                        eventCalendarId,
                        card.id,
                        editingContent.id,
                        payload,
                    )
                    message.success('Card content updated')
                } else {
                    const payload = formatCardContentCreatePayload(values, card.id)
                    await networkInstance.clientWithHeaders.shrineApi.createCardContent(
                        eventCalendarId,
                        card.id,
                        payload,
                    )
                    message.success('Card content created')
                }

                closeContentDrawer()
                fetchCardContents()
            } catch (err) {
                message.error('Failed to save card content')
                console.error(err)
            } finally {
                setSubmittingContent(false)
            }
        },
        [
            editingContent,
            networkInstance,
            eventCalendarId,
            card?.id,
            closeContentDrawer,
            fetchCardContents,
            submittingContent,
        ],
    )

    const columns: ColumnsType<CardContentRow> = [
        {
            title: 'S.No',
            key: 'index',
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
        },
        {
            title: 'Language',
            dataIndex: 'language_code',
            key: 'language_code',
            render: (_val, record) =>
                record.language_code || (record as any).langiuage_code || '-',
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            width: 80,
            render: val =>
                val ? <Tag color='green'>Yes</Tag> : <Tag color='red'>No</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type='link'
                    icon={<EditOutlined />}
                    onClick={() => openEditContent(record)}
                >
                    Edit
                </Button>
            ),
        },
    ]

    return (
        <Drawer
            title={`Card Contents - ${card?.card_type} (${card?.id})`}
            width={900}
            visible={visible}
            onClose={onClose}
            destroyOnClose
            extra={
                <DrawerActions
                    isRecommendationCard={isRecommendation}
                    savingRecommendations={savingRecommendations}
                    onSaveRecommendations={saveRecommendations}
                    onAddContent={openCreateContent}
                />
            }
        >
            {isRecommendation ? (
                <Form layout='vertical'>
                    <Form.Item
                        label={
                            card?.card_type === CARD_TYPES.RECOMMEND_PUJA
                                ? 'Recommended Puja Stores'
                                : 'Recommended Chadhava Stores'
                        }
                    >
                        <Select
                            mode='multiple'
                            allowClear
                            showSearch
                            placeholder={
                                loadingStores ? 'Loading stores...' : 'Select stores'
                            }
                            loading={loadingStores || loadingRecommendations}
                            value={selectedEntityIds}
                            onChange={values => setSelectedEntityIds(values)}
                            options={storeOptions.map(s => ({
                                label: s.title,
                                value: s.id,
                            }))}
                            optionFilterProp='label'
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Form>
            ) : (
                <>
                    <Table<CardContentRow>
                        rowKey={(record, index) =>
                            record.id || `${record.card_id}-${index}`
                        }
                        loading={loadingContents}
                        columns={columns}
                        dataSource={cardContents}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 800 }}
                    />

                    <Drawer
                        title={editingContent ? 'Edit Card Content' : 'Create Card Content'}
                        width={520}
                        visible={contentDrawerOpen}
                        onClose={closeContentDrawer}
                        destroyOnClose
                    >
                        <CardContentForm
                            form={form}
                            onFinish={handleSubmitContent}
                            onCancel={closeContentDrawer}
                            isEditing={!!editingContent}
                            cdnUrl={cdnUrl}
                            preSignUrl={preSignUrl}
                            submitting={submittingContent}
                        />
                    </Drawer>
                </>
            )}
        </Drawer>
    )
}

export default CardContentsDrawer
